import { useState } from "react";
import { importUnitsFile } from "../services/importService";
import { toast } from "react-toastify";
import Button from "./ui/Button";
import FileUpload from "./ui/FileUpload";
import FormField from "./ui/FormField";
import { Download, AlertCircle, CheckCircle } from "lucide-react";
import { unitImportSchema, zodErrorToFirstMessage } from "../schemas/formSchemas";
import styles from "./BulkImport.module.css";

export default function BulkImport({ societyId, onSuccess }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);

  const handleUpload = async () => {
    const parsed = unitImportSchema.safeParse({ file, societyId });
    if (!parsed.success) {
      toast.error(zodErrorToFirstMessage(parsed.error));
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const data = await importUnitsFile(
        parsed.data.file,
        { societyId: parsed.data.societyId },
        (event) => {
          if (event.total) {
            const percent = Math.round((event.loaded * 100) / event.total);
            setProgress(percent);
          }
        }
      );

      setResult(data);

      const parts = [];
      if (data?.insertedCount) parts.push(`Inserted: ${data.insertedCount}`);
      if (data?.updatedCount) parts.push(`Updated: ${data.updatedCount}`);
      if (data?.reactivatedCount) parts.push(`Reactivated: ${data.reactivatedCount}`);
      if (data?.mergedGroupCount) parts.push(`Merged groups: ${data.mergedGroupCount}`);
      if (parts.length) toast.info(parts.join(" | "));

      if (data?.failedCount) {
        toast.warn(`Conflicts / Skipped rows: ${data.failedCount}`);
      }

      onSuccess?.(data);
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  return (
    <div className={styles.card} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div>
        <h2 className={styles.title} style={{ margin: "0 0 6px 0", fontSize: "18px", fontWeight: 700 }}>
          Bulk Import Units
        </h2>
        <p className={styles.hint} style={{ margin: 0, fontSize: "13px", color: "var(--color-text-muted)" }}>
          Required columns: <code style={{ background: "#eff6ff", color: "#1e3a8a", border: "1px solid #bfdbfe", padding: "2px 6px", borderRadius: "6px", fontWeight: 600, fontSize: "12px", fontFamily: "monospace" }}>tower_name</code>,{" "}
          <code style={{ background: "#eff6ff", color: "#1e3a8a", border: "1px solid #bfdbfe", padding: "2px 6px", borderRadius: "6px", fontWeight: 600, fontSize: "12px", fontFamily: "monospace" }}>floor_number</code>,{" "}
          <code style={{ background: "#eff6ff", color: "#1e3a8a", border: "1px solid #bfdbfe", padding: "2px 6px", borderRadius: "6px", fontWeight: 600, fontSize: "12px", fontFamily: "monospace" }}>flat_number</code> (optional:{" "}
          <code style={{ background: "#eff6ff", color: "#1e3a8a", border: "1px solid #bfdbfe", padding: "2px 6px", borderRadius: "6px", fontWeight: 600, fontSize: "12px", fontFamily: "monospace" }}>unit_type</code>, <code style={{ background: "#eff6ff", color: "#1e3a8a", border: "1px solid #bfdbfe", padding: "2px 6px", borderRadius: "6px", fontWeight: 600, fontSize: "12px", fontFamily: "monospace" }}>merged_from</code>)
        </p>
      </div>

      <FormField label="Spreadsheet File" required helperText="CSV or Excel document up to 10MB">
        <FileUpload
          accept=".csv,.xlsx,.xls"
          maxSizeMB={10}
          helperText="Upload unit mapping file"
          onChange={(selectedFile) => setFile(selectedFile)}
        />
      </FormField>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", marginTop: "8px" }}>
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const a = document.createElement("a");
              a.href = "/sample-units.csv";
              a.download = "sample-units.csv";
              a.click();
            }}
          >
            <Download size={14} style={{ marginRight: "4px" }} /> Sample CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const a = document.createElement("a");
              a.href = "/sample-units.xlsx";
              a.download = "sample-units.xlsx";
              a.click();
            }}
          >
            <Download size={14} style={{ marginRight: "4px" }} /> Sample Excel
          </Button>
        </div>

        <Button onClick={handleUpload} disabled={loading || !file} loading={loading}>
          Import Units
        </Button>
      </div>

      {loading && (
        <div className={styles.progressWrap} style={{ marginTop: "12px" }}>
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className={styles.progressText}>{progress}% uploaded</p>
        </div>
      )}

      {result && (
        <div className={styles.result} style={{ marginTop: "16px", padding: "14px", borderRadius: "10px", background: "var(--color-surface-2)", border: "1px solid var(--color-border)" }}>
          <div style={{ display: "flex", gap: "16px", marginBottom: "8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "var(--color-success)", fontWeight: 600 }}>
              <CheckCircle size={16} /> Success: {result.successCount ?? 0}
            </div>
            {result.failedCount > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "var(--color-danger)", fontWeight: 600 }}>
                <AlertCircle size={16} /> Failed: {result.failedCount}
              </div>
            )}
          </div>

          {result.errors?.length > 0 && (
            <div className={styles.errorBox} style={{ marginTop: "10px" }}>
              <table className={styles.table}>
                <thead className={styles.thead}>
                  <tr>
                    <th className={styles.th}>Row</th>
                    <th className={styles.th}>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {result.errors.map((e, idx) => (
                    <tr key={idx} className={styles.row}>
                      <td className={styles.td}>{e.row}</td>
                      <td className={[styles.td, styles.reason].join(" ")}>
                        {e.reason}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
